import type { ComposerSkillId } from '@/lib/api/skills';
import type { OnboardingCreationModule } from '@/lib/api/users';
import type {
  ConversationCapabilitySlotKey,
  ImageAspectRatio,
  ImageModel,
  ImageOutputLimit,
  ImageResolution,
  VideoDuration,
} from '@/types/chat';

export type { ConversationCapabilitySlotKey } from '@/types/chat';

export type ConversationCapabilityMode = 'image' | 'video';
export type ConversationCapabilityCategory = 'skill' | 'imageExtension' | 'videoExtension';
export type ConversationTaskFamilyKey = 'product' | 'person' | 'design' | 'kagemane' | 'reference';
type VisibleConversationSkillId = Exclude<ComposerSkillId, 'brand-visual-video'>;
export type ConversationCapabilityKey =
  | VisibleConversationSkillId
  | 'kagemane'
  | 'buyerShow'
  | 'productDetail'
  | 'poseVariation'
  | 'lookbook'
  | 'outfitMix'
  | 'whiteBackground'
  | 'outfitStyleExtension'
  | 'silhouetteExtension'
  | 'seamlessPattern'
  | 'sketchToProduct'
  | 'productToSketch'
  | 'productVideo'
  | 'modelVideo'
  | 'storyboardBreakdown';
export interface ConversationCapabilitySlot {
  key: ConversationCapabilitySlotKey;
  labelKey: string;
  required: boolean;
  mediaType?: 'image' | 'video';
}

export interface ConversationCapabilityImageDefaults {
  model: ImageModel;
  resolution: ImageResolution;
  aspectRatio: ImageAspectRatio;
  outputCount: ImageOutputLimit;
  /** Suggested quantity presets; they do not cap the total requested output count. */
  allowedOutputCounts: readonly ImageOutputLimit[];
}

export interface ConversationCapabilityVideoDefaults {
  durationSeconds: VideoDuration;
}

export interface ConversationCapabilityContract {
  key: ConversationCapabilityKey;
  family: ConversationTaskFamilyKey;
  category: ConversationCapabilityCategory;
  mode: ConversationCapabilityMode;
  defaultPromptKey: string;
  promptNamespace: 'explore' | 'workflow';
  compactHintKey: string;
  slots: readonly ConversationCapabilitySlot[];
  imageDefaults?: ConversationCapabilityImageDefaults;
  videoDefaults?: ConversationCapabilityVideoDefaults;
  execution:
    | { kind: 'general' }
    | { kind: 'skill'; skillId: ComposerSkillId }
    | { kind: 'delivery'; skillId: ConversationDeliverySkillId }
    | {
      kind: 'template';
      templateCapability:
        | 'product_detail_deck'
        | 'white_background_delivery'
        | 'outfit_styling_lookbook';
      variantKey?: string;
    };
}

export interface ConversationCapabilitySlotAttachment {
  mediaType: string;
  capabilitySlotKey?: string;
}

export interface ConversationCapabilitySlotResolution<
  TAttachment extends ConversationCapabilitySlotAttachment,
> {
  assignments: Partial<Record<ConversationCapabilitySlotKey, TAttachment>>;
  orderedAttachments: TAttachment[];
  unassignedAttachments: TAttachment[];
  missingRequiredSlot: ConversationCapabilitySlot | null;
}

export type ConversationDeliverySkillId =
  | 'conversation-buyer-show'
  | 'conversation-product-detail'
  | 'conversation-outfit-mix'
  | 'conversation-lookbook'
  | 'conversation-white-background'
  | 'conversation-outfit-style-extension'
  | 'conversation-silhouette-extension'
  | 'conversation-seamless-pattern'
  | 'conversation-sketch-to-product'
  | 'conversation-product-to-sketch'
  | 'conversation-product-video'
  | 'conversation-model-video'
  | 'conversation-pose-change'
  | 'conversation-storyboard-breakdown'
  | 'conversation-ugc-video-extension';
export type ConversationDeliveryVideoSkillId = Extract<
  ConversationDeliverySkillId,
  | 'conversation-product-video'
  | 'conversation-model-video'
  | 'conversation-storyboard-breakdown'
>;

const PRODUCT_AND_OPTIONAL_PERSON_SLOTS = [
  { key: 'product', labelKey: 'deliveryComposerSlotProduct', required: true },
  { key: 'person', labelKey: 'deliveryComposerSlotPerson', required: false },
] as const satisfies readonly ConversationCapabilitySlot[];

export const CONVERSATION_CAPABILITY_CONTRACTS = {
  kagemane: {
    key: 'kagemane', family: 'kagemane', category: 'videoExtension', mode: 'video',
    defaultPromptKey: 'deliveryComposerKagemanePrompt', promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerKagemaneHint', slots: [], execution: { kind: 'general' },
  },
  'ugc-bag-unboxing': {
    key: 'ugc-bag-unboxing',
    family: 'kagemane',
    category: 'skill',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptUgcBagUnboxing',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintUgcBagUnboxing',
    slots: PRODUCT_AND_OPTIONAL_PERSON_SLOTS,
    videoDefaults: { durationSeconds: '8' },
    execution: { kind: 'skill', skillId: 'ugc-bag-unboxing' },
  },
  'ugc-beauty-skincare': {
    key: 'ugc-beauty-skincare',
    family: 'kagemane',
    category: 'skill',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptUgcBeautySkincare',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintUgcBeautySkincare',
    slots: PRODUCT_AND_OPTIONAL_PERSON_SLOTS,
    videoDefaults: { durationSeconds: '8' },
    execution: { kind: 'skill', skillId: 'ugc-beauty-skincare' },
  },
  'ugc-try-on': {
    key: 'ugc-try-on',
    family: 'kagemane',
    category: 'skill',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptUgcTryOn',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintUgcTryOn',
    slots: PRODUCT_AND_OPTIONAL_PERSON_SLOTS,
    videoDefaults: { durationSeconds: '8' },
    execution: { kind: 'skill', skillId: 'ugc-try-on' },
  },
  buyerShow: {
    key: 'buyerShow',
    family: 'product',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptBuyerShow',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintBuyerShow',
    slots: [{ key: 'product', labelKey: 'deliveryComposerSlotProduct', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: '3:4',
      outputCount: 4,
      allowedOutputCounts: [1, 2, 3, 4],
    },
    execution: { kind: 'delivery', skillId: 'conversation-buyer-show' },
  },
  productDetail: {
    key: 'productDetail',
    family: 'product',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptProductDetail',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintProductDetail',
    slots: [{ key: 'product', labelKey: 'deliveryComposerSlotProduct', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: '3:4',
      outputCount: 4,
      allowedOutputCounts: [4],
    },
    execution: { kind: 'template', templateCapability: 'product_detail_deck' },
  },
  poseVariation: {
    key: 'poseVariation',
    family: 'person',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'poseChangeDefaultPrompt',
    promptNamespace: 'workflow',
    compactHintKey: 'deliveryComposerCompactHintPoseVariation',
    slots: [
      { key: 'person', labelKey: 'deliveryComposerSlotPerson', required: true },
      { key: 'pose', labelKey: 'deliveryComposerSlotPose', required: true },
    ],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: 'auto',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-pose-change' },
  },
  lookbook: {
    key: 'lookbook',
    family: 'person',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptLookbook',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintLookbook',
    slots: [{ key: 'outfit', labelKey: 'deliveryComposerSlotOutfit', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: 'auto',
      outputCount: 1,
      allowedOutputCounts: [1, 2, 3, 4, 5, 6, 7, 8],
    },
    execution: { kind: 'delivery', skillId: 'conversation-lookbook' },
  },
  outfitMix: {
    key: 'outfitMix',
    family: 'person',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptOutfitMix',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintOutfitMix',
    slots: [
      { key: 'person', labelKey: 'deliveryComposerSlotPerson', required: true },
      { key: 'garment1', labelKey: 'deliveryComposerSlotGarmentOne', required: true },
      { key: 'garment2', labelKey: 'deliveryComposerSlotGarmentTwo', required: false },
      { key: 'garment3', labelKey: 'deliveryComposerSlotGarmentThree', required: false },
      { key: 'garment4', labelKey: 'deliveryComposerSlotGarmentFour', required: false },
      { key: 'garment5', labelKey: 'deliveryComposerSlotGarmentFive', required: false },
    ],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: '3:4',
      outputCount: 1,
      allowedOutputCounts: [1, 2, 3, 4, 5, 6, 7, 8],
    },
    execution: { kind: 'delivery', skillId: 'conversation-outfit-mix' },
  },
  whiteBackground: {
    key: 'whiteBackground',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptWhiteBackground',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintWhiteBackground',
    slots: [{ key: 'product', labelKey: 'deliveryComposerSlotProduct', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: '3:4',
      outputCount: 4,
      allowedOutputCounts: [1, 2, 3, 4],
    },
    execution: { kind: 'delivery', skillId: 'conversation-white-background' },
  },
  outfitStyleExtension: {
    key: 'outfitStyleExtension',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptOutfitStyleExtension',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintOutfitStyleExtension',
    slots: [{ key: 'outfit', labelKey: 'deliveryComposerSlotOutfit', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '2K',
      aspectRatio: '1:1',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-outfit-style-extension' },
  },
  silhouetteExtension: {
    key: 'silhouetteExtension',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptSilhouetteExtension',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintSilhouetteExtension',
    slots: [{ key: 'garment', labelKey: 'deliveryComposerSlotGarment', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '2K',
      aspectRatio: '1:1',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-silhouette-extension' },
  },
  seamlessPattern: {
    key: 'seamlessPattern',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptSeamlessPattern',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintSeamlessPattern',
    slots: [{ key: 'pattern', labelKey: 'deliveryComposerSlotPattern', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: '1:1',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-seamless-pattern' },
  },
  sketchToProduct: {
    key: 'sketchToProduct',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptSketchToProduct',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintSketchToProduct',
    slots: [{ key: 'sketch', labelKey: 'deliveryComposerSlotSketch', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: 'auto',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-sketch-to-product' },
  },
  productToSketch: {
    key: 'productToSketch',
    family: 'design',
    category: 'imageExtension',
    mode: 'image',
    defaultPromptKey: 'deliveryComposerDefaultPromptProductToSketch',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintProductToSketch',
    slots: [{ key: 'garment', labelKey: 'deliveryComposerSlotGarmentProduct', required: true }],
    imageDefaults: {
      model: 'gpt-image-2.5-flare-low',
      resolution: '1K',
      aspectRatio: 'auto',
      outputCount: 1,
      allowedOutputCounts: [1],
    },
    execution: { kind: 'delivery', skillId: 'conversation-product-to-sketch' },
  },
  productVideo: {
    key: 'productVideo',
    family: 'product',
    category: 'videoExtension',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptProductVideo',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintProductVideo',
    slots: [{ key: 'product', labelKey: 'deliveryComposerSlotProduct', required: true }],
    execution: { kind: 'delivery', skillId: 'conversation-product-video' },
  },
  modelVideo: {
    key: 'modelVideo',
    family: 'person',
    category: 'videoExtension',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptModelVideo',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintModelVideo',
    slots: [{ key: 'person', labelKey: 'deliveryComposerSlotPerson', required: true }],
    execution: { kind: 'delivery', skillId: 'conversation-model-video' },
  },
  storyboardBreakdown: {
    key: 'storyboardBreakdown',
    family: 'reference',
    category: 'videoExtension',
    mode: 'video',
    defaultPromptKey: 'deliveryComposerDefaultPromptStoryboardBreakdown',
    promptNamespace: 'explore',
    compactHintKey: 'deliveryComposerCompactHintStoryboardBreakdown',
    slots: [{
      key: 'reference',
      labelKey: 'deliveryComposerSlotReference',
      required: true,
      mediaType: 'video',
    }],
    execution: { kind: 'delivery', skillId: 'conversation-storyboard-breakdown' },
  },
} as const satisfies Record<ConversationCapabilityKey, ConversationCapabilityContract>;

export const FEATURED_CONVERSATION_TASK_FAMILIES = [
  'product',
  'person',
  'design',
  'kagemane',
] as const satisfies readonly ConversationTaskFamilyKey[];

export interface ConversationTaskFamilyDefinition {
  labelKey: string;
  hintKey: string;
  compactLabelKey: string;
  compactHintKey: string;
  onboardingModule: OnboardingCreationModule | null;
  onboardingDefaultCapability: ConversationCapabilityKey;
}

/**
 * Explore family metadata is shared by the main composer and onboarding guide.
 * Capability membership/order stays in CONVERSATION_CAPABILITY_MENU_BY_FAMILY;
 * this object only owns family copy and the recommended first capability.
 */
export const CONVERSATION_TASK_FAMILY_DEFINITIONS = {
  product: {
    labelKey: 'deliveryComposerTaskFamilyProduct',
    hintKey: 'deliveryComposerTaskFamilyProductHint',
    compactLabelKey: 'deliveryComposerTaskFamilyProductCompact',
    compactHintKey: 'deliveryComposerTaskFamilyProductCompactHint',
    onboardingModule: 'product',
    onboardingDefaultCapability: 'whiteBackground',
  },
  person: {
    labelKey: 'deliveryComposerTaskFamilyPerson',
    hintKey: 'deliveryComposerTaskFamilyPersonHint',
    compactLabelKey: 'deliveryComposerTaskFamilyPersonCompact',
    compactHintKey: 'deliveryComposerTaskFamilyPersonCompactHint',
    onboardingModule: 'people',
    onboardingDefaultCapability: 'lookbook',
  },
  design: {
    labelKey: 'deliveryComposerTaskFamilyDesign',
    hintKey: 'deliveryComposerTaskFamilyDesignHint',
    compactLabelKey: 'deliveryComposerTaskFamilyDesignCompact',
    compactHintKey: 'deliveryComposerTaskFamilyDesignCompactHint',
    onboardingModule: 'design',
    onboardingDefaultCapability: 'outfitStyleExtension',
  },
  kagemane: {
    labelKey: 'deliveryComposerTaskFamilyKagemane',
    hintKey: 'deliveryComposerTaskFamilyKagemaneHint',
    compactLabelKey: 'deliveryComposerTaskFamilyKagemaneCompact',
    compactHintKey: 'deliveryComposerTaskFamilyKagemaneCompactHint',
    onboardingModule: 'ugc',
    onboardingDefaultCapability: 'kagemane',
  },
  reference: {
    labelKey: 'deliveryComposerTaskFamilyReference',
    hintKey: 'deliveryComposerTaskFamilyReferenceHint',
    compactLabelKey: 'deliveryComposerTaskFamilyReferenceCompact',
    compactHintKey: 'deliveryComposerTaskFamilyReferenceCompactHint',
    onboardingModule: null,
    onboardingDefaultCapability: 'storyboardBreakdown',
  },
} as const satisfies Record<ConversationTaskFamilyKey, ConversationTaskFamilyDefinition>;

export const ALL_CONVERSATION_TASK_FAMILIES = [
  ...FEATURED_CONVERSATION_TASK_FAMILIES,
  'reference',
] as const satisfies readonly ConversationTaskFamilyKey[];

export const CONVERSATION_CAPABILITIES_BY_FAMILY = {
  product: ['productDetail', 'buyerShow', 'productVideo'],
  person: ['outfitMix', 'lookbook', 'poseVariation', 'modelVideo'],
  design: [
    'whiteBackground',
    'outfitStyleExtension',
    'silhouetteExtension',
    'seamlessPattern',
    'sketchToProduct',
    'productToSketch',
  ],
  kagemane: ['kagemane'],
  reference: ['storyboardBreakdown'],
} as const satisfies Record<ConversationTaskFamilyKey, readonly ConversationCapabilityKey[]>;

/**
 * Explore surfaces may cross-list a capability without changing its primary
 * execution family. White-background generation belongs to the design
 * contract, but is also a common product-content entry point.
 */
export const CONVERSATION_CAPABILITY_MENU_BY_FAMILY = {
  ...CONVERSATION_CAPABILITIES_BY_FAMILY,
  product: [...CONVERSATION_CAPABILITIES_BY_FAMILY.product, 'whiteBackground'],
} as const satisfies Record<ConversationTaskFamilyKey, readonly ConversationCapabilityKey[]>;

export interface ConversationCapabilityEffectPreviewMedia {
  imageUrl: string;
  inputMediaUrl: string;
  outputMediaUrl: string;
  outputMediaUrls?: readonly string[];
  outputPosterUrl?: string;
  inputMediaType: 'image' | 'video';
  outputMediaType: 'image' | 'video';
  inputMediaFit?: 'cover' | 'contain';
  outputMediaFit?: 'cover' | 'contain';
}

export interface ConversationCapabilityPreviewPresentation {
  coverMediaUrl: string;
  effect: ConversationCapabilityEffectPreviewMedia;
}

/**
 * Explore、登录前 Explore 和画布能力面板共用的展示素材契约。
 * 同一个能力的封面和悬浮前后对比必须从这里读取，避免不同入口各自漂移。
 */
export const CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION = {
  productDetail: {
    coverMediaUrl: '/media-previews/v1/covers/product-detail-rhode.webp',
    effect: {
      imageUrl: '/media-previews/v1/effects/product-detail-output-01.jpg',
      inputMediaUrl: '/media-previews/v1/effects/product-detail-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/product-detail-output-01.jpg',
      outputMediaUrls: [
        '/media-previews/v1/effects/product-detail-output-01.jpg',
        '/media-previews/v1/effects/product-detail-output-02.jpg',
        '/media-previews/v1/effects/product-detail-output-03.jpg',
      ],
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'contain',
    },
  },
  productVideo: {
    coverMediaUrl: '/media-previews/v1/covers/product-video.jpg',
    effect: {
      imageUrl: '/media-previews/v1/covers/product-video.jpg',
      inputMediaUrl: '/media-previews/v1/effects/product-detail-input.jpg',
      outputMediaUrl: '/media-previews/v1/videos/product-video.mp4',
      outputPosterUrl: '/media-previews/v1/covers/product-video.jpg',
      inputMediaType: 'image',
      outputMediaType: 'video',
      inputMediaFit: 'contain',
    },
  },
  modelVideo: {
    coverMediaUrl: '/media-previews/v1/covers/model-video.jpg',
    effect: {
      imageUrl: '/media-previews/v1/covers/model-video.jpg',
      inputMediaUrl: '/media-previews/v1/effects/model-video-input.jpg',
      outputMediaUrl: '/media-previews/v1/videos/model-video.mp4',
      outputPosterUrl: '/media-previews/v1/covers/model-video.jpg',
      inputMediaType: 'image',
      outputMediaType: 'video',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  lookbook: {
    coverMediaUrl: '/media-previews/v1/covers/lookbook.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/lookbook-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/lookbook-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/lookbook-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  outfitMix: {
    coverMediaUrl: '/media-previews/v1/covers/outfit-mix.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/outfit-mix-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/outfit-mix-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/outfit-mix-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  whiteBackground: {
    coverMediaUrl: '/media-previews/v1/covers/white-background.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/white-background-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/white-background-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/white-background-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  outfitStyleExtension: {
    coverMediaUrl: '/media-previews/v1/covers/outfit-style-extension.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/outfit-style-extension-output-01.jpg',
      inputMediaUrl: '/media-previews/v1/effects/outfit-style-extension-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/outfit-style-extension-output-01.jpg',
      outputMediaUrls: [
        '/media-previews/v1/effects/outfit-style-extension-output-01.jpg',
        '/media-previews/v1/effects/outfit-style-extension-output-02.jpg',
        '/media-previews/v1/effects/outfit-style-extension-output-03.jpg',
        '/media-previews/v1/effects/outfit-style-extension-output-04.jpg',
      ],
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  silhouetteExtension: {
    coverMediaUrl: '/media-previews/v1/covers/silhouette-extension.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/silhouette-extension-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/silhouette-extension-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/silhouette-extension-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  seamlessPattern: {
    coverMediaUrl: '/media-previews/v1/covers/seamless-pattern.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/seamless-pattern-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/seamless-pattern-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/seamless-pattern-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'cover',
      outputMediaFit: 'cover',
    },
  },
  sketchToProduct: {
    coverMediaUrl: '/media-previews/v1/covers/sketch-to-product.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/sketch-to-product-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/sketch-to-product-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/sketch-to-product-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'contain',
      outputMediaFit: 'contain',
    },
  },
  productToSketch: {
    coverMediaUrl: '/media-previews/v1/covers/product-to-sketch.jpg',
    effect: {
      imageUrl: '/media-previews/v1/effects/product-to-sketch-output.jpg',
      inputMediaUrl: '/media-previews/v1/effects/product-to-sketch-input.jpg',
      outputMediaUrl: '/media-previews/v1/effects/product-to-sketch-output.jpg',
      inputMediaType: 'image',
      outputMediaType: 'image',
      inputMediaFit: 'contain',
      outputMediaFit: 'contain',
    },
  },
} as const satisfies Partial<
  Record<ConversationCapabilityKey, ConversationCapabilityPreviewPresentation>
>;

export function getConversationCapabilityPreviewPresentation(
  capability: ConversationCapabilityKey,
): ConversationCapabilityPreviewPresentation | null {
  return (
    CONVERSATION_CAPABILITY_PREVIEW_PRESENTATION as Partial<
      Record<ConversationCapabilityKey, ConversationCapabilityPreviewPresentation>
    >
  )[capability] ?? null;
}

export function getConversationCapabilityContract(
  capability: ConversationCapabilityKey,
): ConversationCapabilityContract {
  return CONVERSATION_CAPABILITY_CONTRACTS[capability];
}

export function getConversationCapabilityContractBySkillId(
  skillId: string | null | undefined,
): ConversationCapabilityContract | null {
  const normalizedSkillId = String(skillId || '').trim().toLowerCase();
  if (!normalizedSkillId) return null;
  return Object.values(CONVERSATION_CAPABILITY_CONTRACTS).find((contract) => {
    if (!('skillId' in contract.execution)) return false;
    const canonicalSkillId = contract.execution.skillId.toLowerCase();
    return canonicalSkillId === normalizedSkillId
      || canonicalSkillId.replace(/^conversation-/, 'explore-') === normalizedSkillId;
  }) ?? null;
}

export function getConversationCapabilitySkillId(
  capability: ConversationCapabilityKey | null,
): ComposerSkillId | ConversationDeliverySkillId | null {
  if (!capability) return null;
  const execution = getConversationCapabilityContract(capability).execution;
  return execution.kind === 'skill' || execution.kind === 'delivery'
    ? execution.skillId
    : null;
}

export function getConversationCapabilityVideoSkillId(
  capability: ConversationCapabilityKey | null,
): ComposerSkillId | ConversationDeliveryVideoSkillId | null {
  if (!capability) return null;
  const contract = getConversationCapabilityContract(capability);
  if (contract.mode !== 'video') return null;
  const execution = contract.execution;
  return execution.kind === 'skill' || execution.kind === 'delivery'
    ? execution.skillId as ComposerSkillId | ConversationDeliveryVideoSkillId
    : null;
}

export function isConversationDeliveryCapability(
  capability: ConversationCapabilityKey | null,
): boolean {
  return Boolean(
    capability
    && getConversationCapabilityContract(capability).execution.kind === 'delivery',
  );
}

function acceptsConversationCapabilitySlot(
  slot: ConversationCapabilitySlot,
  attachment: ConversationCapabilitySlotAttachment,
): boolean {
  return attachment.mediaType === (slot.mediaType ?? 'image');
}

/**
 * Resolve semantic capability slots from a flat composer attachment list.
 *
 * Only explicitly assigned references can fill a semantic slot. Generic
 * composer attachments (including a selected canvas node) remain inline
 * references and never acquire a role merely because they are compatible.
 * Keyed references also never migrate into a different semantic role when the
 * user switches capabilities. Generic attachments stay before keyed slot
 * attachments so their positional image1/image2 references do not change when
 * a product/person/etc. slot is filled or replaced.
 */
export function resolveConversationCapabilitySlots<
  TAttachment extends ConversationCapabilitySlotAttachment,
>(
  slots: readonly ConversationCapabilitySlot[],
  attachments: readonly TAttachment[],
): ConversationCapabilitySlotResolution<TAttachment> {
  const assignments: Partial<Record<ConversationCapabilitySlotKey, TAttachment>> = {};
  const usedAttachments = new Set<TAttachment>();

  for (const slot of slots) {
    const explicitlyAssigned = attachments.find((attachment) => (
      !usedAttachments.has(attachment)
      && attachment.capabilitySlotKey === slot.key
      && acceptsConversationCapabilitySlot(slot, attachment)
    ));
    if (!explicitlyAssigned) continue;
    assignments[slot.key] = explicitlyAssigned;
    usedAttachments.add(explicitlyAssigned);
  }

  const slottedAttachments = slots.flatMap((slot) => {
    const attachment = assignments[slot.key];
    return attachment
      ? [{ ...attachment, capabilitySlotKey: slot.key } as TAttachment]
      : [];
  });
  const unassignedAttachments = attachments.filter((attachment) => !usedAttachments.has(attachment));

  return {
    assignments,
    orderedAttachments: [...unassignedAttachments, ...slottedAttachments],
    unassignedAttachments,
    missingRequiredSlot: slots.find((slot) => slot.required && !assignments[slot.key]) ?? null,
  };
}
